<template>
  <div v-if="transcoding">
    <p class="text-center">
      {{
        'This video is now transcoding. Please reload the page in few minutes to check if it has uploaded correctly.'
          | translate
      }}
    </p>
  </div>
  <div v-else-if="!block.bunny_id && !uploading">
    <div class="custom-file" v-if="!uploading">
      <input
        ref="file"
        type="file"
        class="form-control"
        @change="submit"
        accept="video/mp4,video/x-m4v,video/*"
      />
    </div>
  </div>

  <div v-else-if="uploading">
    <span>{{ 'Uploading' | translate }}:</span>
    <div class="progress" v-if="uploading">
      <div
        class="progress-bar progress-bar-striped progress-bar-animated"
        role="progressbar"
        :style="`width: ${uploadstatus}%`"
        :aria-valuenow="uploadstatus"
        aria-valuemin="0"
        aria-valuemax="100"
      ></div>
    </div>
  </div>
  <div v-else>
    <div class="ratio ratio-16x9">
      <iframe
        :src="`https://iframe.mediadelivery.net/embed/25534/${block.bunny_id}?autoplay=false&preload=false`"
        :key="componentKey"
        loading="lazy"
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowfullscreen="true"
      ></iframe>
    </div>
    <div class="mt-3">
      <div>
        <button
          @click="initiateUpload"
          class="btn btn-sm btn-outline-primary"
          :class="{ disabled: thumbnailUploadStatus !== 0 }"
        >
          <span v-if="thumbnailUploadStatus === 0">
            {{ 'Upload thumbnail' | translate }}
          </span>
          <div v-else class="spinner-border spinner-border-sm" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </button>
      </div>
      <div>
        <small class="form-text text-muted">
          {{ 'Max: 512Kb, 1920x1080' | translate }}
        </small>
      </div>
      <div class="custom-file">
        <input
          ref="thumbnail"
          type="file"
          class="form-control"
          @change="submitThumbnail"
          accept="image/*"
          hidden
        />
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import {Upload} from 'tus-js-client'

export default {
  props: ['block'],
  data() {
    return {
      uploading: false,
      uploadFinished: false,
      transcoding: false,
      working: false,
      uploadstatus: 0,
      offset: 0,
      tusUpload: null,
      player: null,
      file: null,
      thumbnail: null,
      thumbnailUploadStatus: 0,
      componentKey: 1,
      upload: null,
    }
  },
  mounted() {
    if (this.block.bunny_id && _.includes([0, 1, 2], this.block.bunny_status)) {
      this.transcoding = true
      this.$set(this.block, 'transcoding', true)
    }
  },
  methods: {
    forceRerender() {
      this.componentKey += 1
    },

    initiateUpload() {
      this.$refs.thumbnail.click()
    },

    submitThumbnail() {
      this.thumbnail = this.$refs['thumbnail'].files[0]
      if (!this.thumbnail || this.thumbnail.size > 5_512_001) {
        iziToast.warning({
          title: 'Warning',
          message: this.$t('validation.max.file', {
            attribute: 'file',
            max: 512,
          }),
          position: 'bottomLeft',
        })
        return null
      }

      this.thumbnailUploadStatus = 1

      axios
        .post(`/home/blocks/${this.block.id}/set-thumbnail`, {
          extension: this.thumbnail.name.split('.').pop(),
        })
        .then((response) => {
          let config = {
            headers: {
              'Content-Type': this.thumbnail.type,
            },
            transformRequest: [
              (data, headers) => {
                delete headers.common.Authorization
                delete headers.common['X-CSRF-TOKEN']
                return data
              },
            ],
          }
          return axios.put(response.data.upload, this.thumbnail, config)
        })
        .then(() => {
          return axios.post(
            `/home/blocks/${this.block.id}/set-thumbnail-at-bunny`,
          )
        })
        .then((response) => {
          this.thumbnailUploadStatus = 0
          this.forceRerender()
        })
        .catch((error) => {
          let status = _.get(error, 'response.status')
          if (status === 419) {
            this.$root.$emit(
              'flash',
              this.$t('Your session has expired - please reload'),
              'warning',
            )
          } else {
            console.error(error)
          }
        })
    },
    initiateTusUpload(response) {
      this.upload = new Upload(this.file, {
        endpoint: response.data.endpoint,
        retryDelays: [0, 3000, 5000, 10000, 20000, 60000, 60000],
        headers: {
          AuthorizationSignature: response.data.AuthorizationSignature,
          AuthorizationExpire: response.data.AuthorizationExpire,
          VideoId: response.data.VideoId,
          LibraryId: response.data.LibraryId,
        },
        metadata: {
          filetype: this.file.type,
          title: response.data.title,
        },
        onError: (error) => {
          iziToast.error({
            title: 'Error',
            message: this.$t('Something went wrong'),
            position: 'bottomLeft',
          })
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          this.uploadstatus = Math.ceil((bytesUploaded / bytesTotal) * 100)
        },
        onSuccess: () => {
          this.uploading = false
          this.uploadFinished = true
          this.transcoding = true
          this.$set(this.block, 'bunny_id', response.data.VideoId)
          this.$set(this.block, 'transcoding', true)

          iziToast.success({
            title: 'OK',
            message: this.$t('Saved'),
            position: 'bottomLeft',
          })
        },
      })

      this.upload.findPreviousUploads().then((previousUploads) => {
        if (previousUploads.length) {
          this.upload.resumeFromPreviousUpload(previousUploads[0])
        }
        this.uploading = true
        this.upload.start()
      })
    },
    submit() {
      this.file = this.$refs['file'].files[0]

      if (!this.file) {
        return null
      }

      this.uploading = true
      let that = this
      axios
        .post(`/home/blocks/${this.block.id}/create-video`, {
          size: this.file.size,
          extension: this.file.name.split('.').pop(),
        })
        .then(this.initiateTusUpload)
        .catch((error) => {
          let status = _.get(error, 'response.status')
          if (status === 419) {
            iziToast.warning({
              title: 'Warning',
              message: this.$t('Your session has expired - please reload'),
              position: 'bottomLeft',
            })
          } else {
            console.error(error)
          }
        })
    },
  },
}
</script>
